import { calculateRegulatoryDeadlines } from './regulatoryDeadlines';

/**
 * Utility functions for managing timeline updates when regulatory triggers change
 */

/**
 * Calculate the difference between old and new regulatory deadlines
 * @param {Date|Timestamp} occurredAt - When the incident occurred
 * @param {Array} regulatoryRules - Organization's regulatory rules
 * @param {string} incidentType - Type of incident
 * @param {Array} oldTriggers - Previously selected triggers
 * @param {Array} newTriggers - Newly selected triggers
 * @returns {Object} Object containing added and removed deadlines
 */
export const calculateTimelineChanges = (occurredAt, regulatoryRules, incidentType, oldTriggers = [], newTriggers = []) => {
  if (!occurredAt || !regulatoryRules || !Array.isArray(regulatoryRules)) {
    return { addedDeadlines: [], removedDeadlines: [] };
  }

  // Calculate deadlines for old and new triggers
  const oldDeadlines = calculateRegulatoryDeadlines(occurredAt, regulatoryRules, incidentType, oldTriggers);
  const newDeadlines = calculateRegulatoryDeadlines(occurredAt, regulatoryRules, incidentType, newTriggers);

  // Find added deadlines (in new but not in old)
  const addedDeadlines = newDeadlines.filter(newDeadline => 
    !oldDeadlines.some(oldDeadline => 
      oldDeadline.trigger === newDeadline.trigger && 
      oldDeadline.regulator === newDeadline.regulator
    )
  );

  // Find removed deadlines (in old but not in new)
  const removedDeadlines = oldDeadlines.filter(oldDeadline => 
    !newDeadlines.some(newDeadline => 
      newDeadline.trigger === oldDeadline.trigger && 
      newDeadline.regulator === oldDeadline.regulator
    )
  );

  return {
    addedDeadlines,
    removedDeadlines,
    allNewDeadlines: newDeadlines
  };
};

/**
 * Get triggers that were added or removed
 * @param {Array} oldTriggers - Previously selected triggers
 * @param {Array} newTriggers - Newly selected triggers
 * @returns {Object} Object containing added and removed triggers
 */
export const getTriggerChanges = (oldTriggers = [], newTriggers = []) => {
  const addedTriggers = newTriggers.filter(trigger => !oldTriggers.includes(trigger));
  const removedTriggers = oldTriggers.filter(trigger => !newTriggers.includes(trigger));

  return {
    addedTriggers,
    removedTriggers,
    hasChanges: addedTriggers.length > 0 || removedTriggers.length > 0
  };
};

/**
 * Format timeline change summary for user feedback
 * @param {Object} changes - Changes object from getTriggerChanges
 * @param {Array} regulatoryRules - Organization's regulatory rules
 * @returns {string} Human-readable summary of changes
 */
export const formatTimelineChangeSummary = (changes, regulatoryRules) => {
  const { addedTriggers, removedTriggers } = changes;
  const messages = [];

  if (addedTriggers.length > 0) {
    const addedRules = regulatoryRules.filter(rule => addedTriggers.includes(rule.trigger));
    const regulatorNames = addedRules.map(rule => rule.regulator);
    messages.push(`Added regulatory deadlines for: ${regulatorNames.join(', ')}`);
  }

  if (removedTriggers.length > 0) {
    const removedRules = regulatoryRules.filter(rule => removedTriggers.includes(rule.trigger));
    const regulatorNames = removedRules.map(rule => rule.regulator);
    messages.push(`Removed regulatory deadlines for: ${regulatorNames.join(', ')}`);
  }

  return messages.join('. ');
};

/**
 * Validate that timeline updates are consistent with regulatory changes
 * @param {Object} incident - Incident data
 * @param {Array} regulatoryRules - Organization's regulatory rules
 * @param {Array} selectedTriggers - User-selected triggers
 * @returns {boolean} True if timeline should be updated
 */
export const shouldUpdateTimeline = (incident, regulatoryRules, selectedTriggers) => {
  // Only update if incident has occurred and we have regulatory rules
  return !!(incident?.occurredAt && regulatoryRules?.length > 0);
}; 